<template>
  <div class="w-full">
    <UploadVideoFile
      v-if="
        androidWatcfaceOS == 'not-android-device' ||
        androidWatcfaceOS == 'android'
      "
      :video-name="currentVideoName"
      :duration="shape.type == ShapeType.VideoWatchface ? 3 : undefined"
      :label="label"
      :disabled="false"
      @upload="onAddVideo"
      @delete="onDeleteVideo"
    />

    <!-- 预览图不区分配色 -->
    <ShapePreviewImage
      class="mt-2.5"
      :shape="shape"
      :preview-key="spriteItem.videoPreviewName ?? 'videoPreview'"
      :disabled="false"
      :tooltip="
        androidWatcfaceOS == 'not-android-device'
          ? '预览图为必传'
          : '在轻智能设备上展示预览图，预览图为必传'
      "
    />
  </div>
</template>

<script lang="ts" setup>
import { useWatchfaceAndroid } from '@/hooks';
import { Shape, ShapeType } from '@/services/shape';
import { SpriteAttribute } from '@/services/shape/attributes/sprite';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { computed } from 'vue';
import { PropType } from 'vue';

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  },
  spriteItem: {
    type: Object as PropType<SpriteAttribute['spriteExtra'][number]>,
    required: true
  },
  label: {
    type: String,
    default: '上传视频'
  },
  imageLabel: {
    type: String,
    default: '上传预览图'
  }
});

const editorStore = useEditorStore();
const watchfaceStore = useWatchfaceStore();
const { androidWatcfaceOS } = useWatchfaceAndroid();

const colorId = computed(() => {
  const colorId =
    watchfaceStore.colorMode == 'recolor'
      ? editorStore.theme?.colorGroupTable?.[0]?.id
      : editorStore.themeColor?.id;
  return colorId ?? '';
});

/** 获取当前样式下的视频名称 */
const currentVideoName = computed(() => {
  const id = props.spriteItem.id;
  const cid = colorId.value;
  if (!id) {
    // 主视频：优先从 videoColorData 读取当前样式数据
    const colorData = (props.shape.attrs as SpriteAttribute).videoColorData;
    if (colorData) {
      if (colorData[cid]?.videoName) {
        return colorData[cid].videoName ?? '';
      }
      // 当前样式无数据时，fallback 到任意已有样式的数据
      const firstKey = Object.keys(colorData).find(
        (k) => !!colorData[k]?.videoName
      );
      if (firstKey) {
        return colorData[firstKey].videoName ?? '';
      }
    }
    return (props.shape.attrs as SpriteAttribute).videoName ?? '';
  } else {
    // spriteExtra 中的视频
    const colorData = props.spriteItem.videoColorData;
    if (colorData) {
      if (colorData[cid]?.videoName) {
        return colorData[cid].videoName ?? '';
      }
      // 当前样式无数据时，fallback 到任意已有样式的数据
      const firstKey = Object.keys(colorData).find(
        (k) => !!colorData[k]?.videoName
      );
      if (firstKey) {
        return colorData[firstKey].videoName ?? '';
      }
    }
    return props.spriteItem.videoName ?? '';
  }
});

const onDeleteVideo = () => {
  const shape = props.shape;
  const id = props.spriteItem.id;
  const cid = colorId.value;

  if (!id) {
    // 主视频：清除当前样式的视频数据，同时清除旧字段
    const videoColorData = {
      ...((shape.attrs as SpriteAttribute).videoColorData ?? {}),
      [cid]: { videoSrc: '', videoName: '', videoFps: 0 }
    };
    shape.setAttrs({
      videoColorData,
      videoSrc: '',
      videoName: '',
      videoFps: 0
    });
  } else {
    // spriteExtra 中的视频
    shape.setAttrs({
      spriteExtra: (shape.attrs as SpriteAttribute).spriteExtra?.map((item) => {
        if (item.id == id) {
          const videoColorData = {
            ...(item.videoColorData ?? {}),
            [cid]: { videoSrc: '', videoName: '', videoFps: 0 }
          };
          return {
            ...item,
            videoColorData,
            videoSrc: '',
            videoName: '',
            videoFps: 0,
            w: shape.attrs.w ?? 0,
            h: shape.attrs.h ?? 0
          };
        } else {
          return item;
        }
      })
    });
  }
};

const onAddVideo = (data: {
  videoSrc: string;
  videoName: string;
  videoFps: number;
  w: number;
  h: number;
}) => {
  const { videoSrc, videoName, videoFps, w, h } = data;
  const id = props.spriteItem.id;
  const cid = colorId.value;

  if (!id) {
    const videoColorData = {
      ...((props.shape.attrs as SpriteAttribute).videoColorData ?? {}),
      [cid]: { videoSrc, videoName, videoFps }
    };
    props.shape.setAttrs({
      videoColorData,
      videoSrc,
      videoName,
      videoFps,
      w,
      h
    });
  } else {
    props.shape.setAttrs({
      w,
      h,
      spriteExtra: (props.shape.attrs as SpriteAttribute).spriteExtra?.map(
        (item) => {
          if (item.id == id) {
            const videoColorData = {
              ...(item.videoColorData ?? {}),
              [cid]: { videoSrc, videoName, videoFps }
            };
            return {
              ...item,
              videoColorData,
              videoSrc,
              videoName,
              videoFps,
              w,
              h
            };
          } else {
            return item;
          }
        }
      )
    });
  }
};
</script>
